import { observer } from 'mobx-react-lite';
import { addLocationTextConsumer } from 'owa-locstrings/lib/strings/addlocationtextconsumer.locstring.json';
import { addLocationText } from 'owa-locstrings/lib/strings/addlocationtext.locstring.json';
import loc from 'owa-localize';
/* tslint:disable:jsx-no-lambda WI:47743 */
import OnlineMeetingElement from 'owa-calendar-meeting-forms-common-view/lib/components/OnlineMeetingElement';
import { Icon } from '@fluentui/react/lib/Icon';
import type { ITheme } from '@fluentui/react/lib/Styling';
import { getComposeEvent, getFullComposeViewState } from 'owa-calendar-compose-store-manager';
import generateBorderStyle from 'owa-calendar-forms-common/lib/utils/generateBorderStyle';
import { renderLocationFBBadge } from 'owa-calendar-compose-form-view/lib/components/renderLocationFBBadge';
import SidePaneType from 'owa-calendar-sidepane/lib/schema/SidePaneType';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import { isConsumer } from 'owa-session-store';
import LocationCommonWell from 'owa-location-common-well/lib/components/LocationCommonWell';
import getLocationCommonWellItemsFromEnhancedLocation from 'owa-location-common-well/lib/utils/enhancedLocationConversion';
import { default as getLocationSuggestionsCache } from 'owa-location-suggestions-cache/lib/store/store';
import { onLocationSuggestionsStateChanged } from 'owa-location-suggestions-cache/lib/utils/onLocationSuggestionsStateChanged';
import { populateLocationSuggestions } from 'owa-location-suggestions-cache/lib/utils/populateLocationSuggestions';
import { isFeatureEnabled } from 'owa-feature-flags';

import * as React from 'react';
import {
    addLocationsFromCommonWell,
    removeLocationsFromCommonWell,
    updateSidePaneType,
    setRoomBookingAssistantEnabled,
} from 'owa-calendar-compose-form-actions';
import { updateUnresolvedLocation } from 'owa-calendar-compose-form-store/lib/mutators/composeViewStateMutators';
import RoomBookingAssistantButton from './RoomBookingAssistantButton';
import RoomBookingAssistantContent from './RoomBookingAssistantContent';
import shouldSupportAutoRoomBooking from 'owa-calendar-compose-form-store/lib/selectors/shouldSupportAutoRoomBooking';
import { updateAutoRoomBookingMetric } from 'owa-calendar-compose-form-store/lib/mutators/eventMetricsMutators';
import { getCalendarThemedButtonStyle } from 'owa-component-styling-utils';
import type LocationCommonWellItemViewState from 'owa-location-common-well/lib/store/schema/LocationCommonWellItemViewState';
import { userInteractionAction } from 'owa-calendar-actions';

import styles from './FullComposeLeftPane.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface LocationRowProps {
    calendarColorScheme: ColorScheme;
    theme: ITheme;
    formId: string;
}

export default observer(function LocationRow(props: LocationRowProps) {
    const { calendarColorScheme, theme, formId } = props;

    React.useEffect(() => {
        // Calling populate the cache for the location well
        const event = getComposeEvent(formId);
        populateLocationSuggestions(event, 'All' /*search mode of the location well in compose*/);
    }, [formId]);

    const onRoomBookingClicked = React.useCallback(() => {
        setRoomBookingAssistantEnabled(true, formId);
        updateAutoRoomBookingMetric(
            {
                automatedRoomBookingChanged: true,
            },
            formId
        );
    }, [formId]);

    const onRoomBookingCloseButtonClicked = React.useCallback(() => {
        setRoomBookingAssistantEnabled(false, formId);
        updateAutoRoomBookingMetric(
            {
                automatedRoomBookingChanged: true,
            },
            formId
        );
    }, [formId]);

    const onItemsAdded = React.useCallback(
        (newLocations: LocationCommonWellItemViewState[]) => {
            addLocationsFromCommonWell(newLocations, formId);
        },
        [formId]
    );

    const showRoomSelector = React.useCallback(() => {
        updateSidePaneType(formId, SidePaneType.RoomList);
    }, [formId]);

    const onItemsRemoved = React.useCallback(
        (removedLocations: LocationCommonWellItemViewState[]) => {
            removeLocationsFromCommonWell(removedLocations, formId);
        },
        [formId]
    );

    const onQueryStringChanged = React.useCallback(
        (query: string) => {
            updateUnresolvedLocation(query, formId);
        },
        [formId]
    );

    const onRenderLocationFBBadge = React.useCallback(
        (emailAddress: string) => {
            return renderLocationFBBadge(emailAddress, formId);
        },
        [formId]
    );

    const event = getComposeEvent(formId);
    const isBusiness = !isConsumer(event.ParentFolderId.mailboxInfo.userIdentity);
    const locationCommonWellPlaceholder = isBusiness
        ? loc(addLocationText)
        : loc(addLocationTextConsumer);
    const borderColorStyle = generateBorderStyle(calendarColorScheme);
    const buttonStyle = getCalendarThemedButtonStyle(calendarColorScheme);
    const fullComposeViewState = getFullComposeViewState(formId);
    const logJoinMeetingTelemetry = () => {
        userInteractionAction('JoinOnlineMeeting', 'EditForm', event.IsOrganizer, event.IsMeeting);
    };
    return (
        <div className={styles.locationRow}>
            <Icon iconName={ControlIcons.POI} className={styles.locationRowIcon} />
            <div className={classNames(styles.locationRowContainer, borderColorStyle.bottomBorder)}>
                {event.IsRoomRequested ? (
                    <RoomBookingAssistantContent
                        event={event}
                        colorScheme={calendarColorScheme}
                        onClick={onRoomBookingCloseButtonClicked}
                    />
                ) : (
                    <LocationCommonWell
                        containerClassName={styles.locationCommonWellContainer}
                        defaultSelectedItems={getLocationCommonWellItemsFromEnhancedLocation(
                            event.Locations
                        )}
                        selectedItems={getLocationCommonWellItemsFromEnhancedLocation(
                            event.Locations
                        )}
                        draggableItemType={DraggableItemTypes.ReadWriteRecipient}
                        dropViewState={null}
                        roomFreeBusyEnabled={true}
                        event={event}
                        theme={theme as any}
                        inputProps={{
                            placeholder:
                                event.Locations.length == 0 ? locationCommonWellPlaceholder : '',
                            'aria-label': locationCommonWellPlaceholder,
                        }}
                        colorScheme={calendarColorScheme}
                        onItemsAdded={onItemsAdded}
                        onItemsRemoved={onItemsRemoved}
                        showAddRoom={isBusiness}
                        onAddRoomClicked={showRoomSelector}
                        onRenderFBBadge={onRenderLocationFBBadge}
                        maxItemWidth={420}
                        getPickerState={() =>
                            getLocationSuggestionsCache().locationSuggestionsState
                        }
                        onPickerStateChanged={onLocationSuggestionsStateChanged}
                        onQueryStringChanged={onQueryStringChanged}
                        shouldClearInput={!fullComposeViewState.unresolvedLocation}
                        canRemoveSuggestion={true}
                        disabled={!fullComposeViewState.uiEnabled}
                        formId={formId}
                    />
                )}
                {!isFeatureEnabled('cal-mf-roomBookingAssistant') && (
                    <OnlineMeetingElement
                        event={event}
                        calendarColorScheme={calendarColorScheme}
                        theme={theme}
                        isDarkMode={fullComposeViewState.isDarkMode}
                        dropdownContainerClassName={
                            styles.onlineMeetingDropdownContainerLocationRow
                        }
                        disabled={!fullComposeViewState.uiEnabled}
                        joinOnlineLinkContainerClassName={styles.joinOnlineLinkContainerLocationRow}
                        formId={formId}
                        onLinkClickedCallback={logJoinMeetingTelemetry}
                    />
                )}
                {isFeatureEnabled('cal-mf-roomBookingAssistant') &&
                    shouldSupportAutoRoomBooking(formId) &&
                    !event.IsRoomRequested && (
                        <RoomBookingAssistantButton
                            onClick={onRoomBookingClicked}
                            buttonStyle={buttonStyle}
                        />
                    )}
            </div>
        </div>
    );
});
